import React, { useRef } from 'react';
import { arc, pie } from 'd3-shape';
import { v4 as uuidv4 } from 'uuid';
import { ChartWrapper, IconOverlay, LargeIcon, SmallIcon } from './elements';

const sizes = {
  small: 56,
  large: 200,
};

const strokeWidthMultiplier = {
  small: 1,
  large: 2,
};

const padAngles = {
  small: 0.05,
  large: 0.03,
};

const icons = {
  small: SmallIcon,
  large: LargeIcon,
};

const thickness = {
  small: 5,
  large: 20,
};

const iconOffset = {
  small: -1,
  large: -7,
};

const PADDING = 2;

type Props = {
  readonly className?: string;
  readonly size: string;
  readonly icon: string;
  readonly iconColor: string;
  readonly data: {
    id: number;
    color: string;
    borderColor: string;
    borderWidth: number;
  }[];
};

export default function StreakCircle(props: Props): React.ReactElement {
  const { current: id } = useRef(uuidv4());
  const size = sizes[props.size];
  const oRadius = size / 2 - PADDING * 2;
  const iRadius = oRadius - thickness[props.size];

  const arcChart = arc().outerRadius(oRadius).innerRadius(iRadius);

  const pieChart = pie()
    .value(() => 1) // All should have the same size
    .sort(null)
    .padAngle(padAngles[props.size]);

  const data = pieChart(props.data);

  const Icon = icons[props.size];

  return (
    <ChartWrapper className={props.className}>
      <svg className={props.className} width={size} height={size}>
        <g transform={`translate(${size / 2}, ${size / 2})`}>
          {data.map(d => {
            const path = arcChart(d);

            return (
              <React.Fragment key={d.data.id}>
                <mask id={`mask-${id}-${d.data.id}`} fill="white">
                  <path d={path} />
                </mask>
                <path
                  d={path}
                  fill={d.data.color}
                  stroke={d.data.borderColor}
                  strokeWidth={
                    d.data.borderWidth * strokeWidthMultiplier[props.size]
                  } // Multiply by 2 as half is cut off by mask (only when large)
                  mask={`url(#mask-${id}-${d.data.id})`}
                />
              </React.Fragment>
            );
          })}
        </g>
      </svg>
      <IconOverlay $iconOffset={iconOffset[props.size]}>
        <Icon icon={props.icon} color={props.iconColor} />
      </IconOverlay>
    </ChartWrapper>
  );
}
